import { useContext, useState } from "react";
import { isValidEmail, toast } from "../../helper/common";
import { sendUniversalMail } from "../../services/sharedService";
import GuarantorInviteSection from "../../components/GuarantorInviteSection/GuarantorInviteSection";
import BasicInfoModal from "../onboarding/BasicInfoModal";
import RequiredDocumentsModal from "../onboarding/RequiredDocumentsModal";
import TenantChecksIncomeModel from "./TenantChecksIncomeModel";
import ApplicationContext from "../../context/application/ApplicationContext";
import TenantContext from "../../context/tenant/TenantContext";
import styles from "../onboarding/CreateApplicationStep.module.css";

export default function TenantChecksGroupStep({
  user,
  formData,
  guarantor,
  guarantorFormData,
  setGuarantorFormData,
  createUserProfile,
  requiresSubcategory,
  originalUser,
  guarantorPhoneNumber,
  setGuarantorPhoneNumber,
  selectedProtectedHousingDocs,
  setSelectedProtectedHousingDocs,
  additionalInfo,
  formHandler,
  setStepId,
  updateLLTenant,
  completeTenantCheck,
  loadData,
  _t,
}) {
  const { createApplication } = useContext(ApplicationContext);
  const { createBasicGuarantorAccount, upsertSelectedOnboardingUsers } =
    useContext(TenantContext);
  const [guarantorStage, setGuarantorStage] = useState("overview");
  const [guarantorInvites, setGuarantorInvites] = useState([]);
  const [guarantorSubmitting, setGuarantorSubmitting] = useState(false);
  const [isEditingTenant, setIsEditingTenant] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // tenant invites — same as CreateApplicationStep
  const [tenantInvites, setTenantInvites] = useState([]);
  const [openTenantForm, setOpenTenantForm] = useState(false);
  const [tenantDraft, setTenantDraft] = useState({
    name: "",
    email: "",
    error: "",
  });

  const tenantName =
    [user?.fname, user?.lname].filter(Boolean).join(" ") ||
    [formData?.fname, formData?.lname].filter(Boolean).join(" ") ||
    _t("tenant", "Tenant");
  const tenantEmail = user?.email || formData?.email || "";
  const tenantInitial = (tenantName?.charAt(0) || "T").toUpperCase();

  const startEditTenant = () => {
    setEditName(tenantName === _t("tenant", "Tenant") ? "" : tenantName);
    setEditEmail(tenantEmail);
    setIsEditingTenant(true);
  };
  const saveTenantEdit = async () => {
    const name = editName.trim();
    const email = editEmail.trim().toLowerCase();
    if (!name || !email) {
      toast.error(_t("nameAndEmailRequired", "Name and email are required"));
      return;
    }
    const [fname = "", ...rest] = name.split(/\s+/);
    const lname = rest.join(" ");
    try {
      setEditSaving(true);
      await updateLLTenant({ id: user?._id, fname, lname, email });
      await loadData(false, user?._id);
      setIsEditingTenant(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || _t("somethingWentWrong"));
    } finally {
      setEditSaving(false);
    }
  };

  const addTenantInvite = () => {
    const trimmedName = tenantDraft.name.trim();
    const trimmedEmail = tenantDraft.email.trim().toLowerCase();
    if (!trimmedName) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("nameRequired", "Full name is required"),
      }));
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("invalidEmail", "Enter a valid email address"),
      }));
      return;
    }
    if (
      trimmedEmail === user?.email?.toLowerCase() ||
      trimmedEmail === originalUser?.email?.toLowerCase()
    ) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("cannotAddYourself", "You cannot add yourself"),
      }));
      return;
    }
    if (
      tenantInvites.some((i) => i.email === trimmedEmail) ||
      guarantorInvites.some((g) => g.email === trimmedEmail)
    ) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("emailAlreadyAdded", "This email has already been added"),
      }));
      return;
    }
    if (tenantInvites.length >= 4) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("maxParticipantsReached", "Maximum of 4 tenants allowed"),
      }));
      return;
    }
    setTenantInvites((c) => [...c, { name: trimmedName, email: trimmedEmail }]);
    setTenantDraft({ name: "", email: "", error: "" });
    setOpenTenantForm(false);
  };

  const submitGuarantors = async (invitesToSubmit) => {
    const emailInvites = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod !== "self"
    );
    const selfManaged = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod === "self"
    );
    for (const g of emailInvites) {
      const [fname, ...surname] = g.name.split(/\s+/);
      try {
        await createBasicGuarantorAccount({
          fname,
          lname: surname.join(" "),
          email: g.email,
          gurantor_relation: g.relation,
          step: 1,
        });
      } catch (err) {
        if (err?.response?.data?.errorKey !== "account-already-present")
          throw err;
      }
      await sendUniversalMail({
        receiverEmail: g.email,
        senderId: localStorage.getItem("saveduserid"),
        emailType: "score",
        status: "processing",
        gurantor_relation: g.relation,
      });
    }
    if (selfManaged.length > 0) {
      const selectedOnboardingUsers = Array.isArray(
        user?.tenant_extra_field_id?.selected_onboarding_users
      )
        ? user.tenant_extra_field_id.selected_onboarding_users
        : [];
      const newUsers = selfManaged
        .map(({ name }) => ({ name, type: "guarantor" }))
        .filter(
          ({ name, type }) =>
            !selectedOnboardingUsers.some(
              (u) => u?.type === type && u?.name === name
            )
        );
      const next = [...selectedOnboardingUsers, ...newUsers];
      const result = await upsertSelectedOnboardingUsers({
        tenant_id: user?._id ?? localStorage.getItem("saveduserid"),
        users: next,
      });
      if (result === null) throw new Error("failed-to-add-guarantor");
    }
  };

  const continueFromGuarantorInvites = async () => {
    if (guarantor) {
      setGuarantorStage("documents");
      return;
    }
    const selfInvites = guarantorInvites.filter(
      ({ guarantorMethod }) => guarantorMethod === "self"
    );
    if (selfInvites.length > 1) {
      toast.error(
        _t(
          "onlyOneGuarantorCanBeCompletedHere",
          "Only one guarantor can be completed in this onboarding"
        )
      );
      return;
    }
    try {
      setGuarantorSubmitting(true);
      await submitGuarantors(guarantorInvites);
      if (selfInvites[0]) {
        const { name, email } = selfInvites[0];
        const [fname = "", ...lastNameParts] = name.trim().split(/\s+/);
        setGuarantorFormData((current) => ({
          ...current,
          email,
          fname,
          lname: lastNameParts.join(" "),
        }));
        setGuarantorStage("details");
        return;
      }
      if (tenantInvites.length > 0) {
        const title = `${tenantName} ${_t("application", "application")}`;
        await createApplication({
          participants: tenantInvites.map(({ name, email }) => ({
            name,
            email,
          })),
          title,
          skor_protect: true,
          description: "",
        });
      }
      await completeTenantCheck();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          _t("somethingWentWrong")
      );
    } finally {
      setGuarantorSubmitting(false);
    }
  };

  const advanceGuarantorDocuments = async () => {
    if (additionalInfo.includes("GUARANTOR_SKOR")) {
      setGuarantorStage("financial");
      return;
    }
    await completeTenantCheck();
  };
  const updateGuarantorProfile = async (data) =>
    updateLLTenant({ ...data, id: guarantor?._id });

  if (guarantorStage === "details") {
    return (
      <BasicInfoModal
        user={guarantor ?? {}}
        formData={guarantorFormData}
        setFormData={setGuarantorFormData}
        createUserProfile={(data) => createUserProfile(data, "llGuarantor")}
        formHandler={{
          prev: () => setGuarantorStage("overview"),
          next: () => setGuarantorStage("documents"),
        }}
        requiresSubcategory={requiresSubcategory}
        originalUser={originalUser}
        phoneNumber={guarantorPhoneNumber}
        setPhoneNumber={setGuarantorPhoneNumber}
        selectedProtectedHousingDocs={selectedProtectedHousingDocs}
        setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
        usedFor="tenantChecks"
        llGuarantor
      />
    );
  }
  if (guarantorStage === "documents" && guarantor) {
    return (
      <RequiredDocumentsModal
        user={guarantor}
        formHandler={{
          prev: () => setGuarantorStage("overview"),
          next: advanceGuarantorDocuments,
        }}
        updateUserProfile={advanceGuarantorDocuments}
        isGuarantor={false}
        needWaitingJobForClave={false}
        isEditMode={false}
        hideClave
        llCreated
        onClose={() => setGuarantorStage("overview")}
      />
    );
  }
  if (guarantorStage === "financial" && guarantor) {
    return (
      <TenantChecksIncomeModel
        user={guarantor}
        additionalInfo={[]}
        isGuarantor
        usedFor="tenantChecks"
        formHandler={{
          prev: () => setGuarantorStage("documents"),
          next: completeTenantCheck,
        }}
        loadGuarantorData={() => loadData(false, user?._id)}
        updateUserProfile={updateGuarantorProfile}
      />
    );
  }

  return (
    <div
      className="popup-overlay wrapper-modal"
      style={{
        alignItems: "flex-start",
        background: "var(--skor-color-canvas, #f6f6f6)",
        display: "flex",
        justifyContent: "center",
        overflowY: "auto",
        padding: "0 16px",
      }}
    >
      <section
        style={{
          background: "#fff",
          border: "1px solid #ececef",
          borderRadius: 18,
          boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
          margin: "clamp(32px, 7vh, 80px) 0 clamp(24px, 3.5vh, 40px)",
          padding: "28px 28px 22px",
          width: "min(720px, 100%)",
        }}
      >
        <button
          type="button"
          onClick={formHandler.prev}
          style={{
            background: "none",
            border: 0,
            color: "#1a2e2a",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1,
            marginBottom: 18,
          }}
        >
          <span aria-hidden="true">‹</span> {_t("back", "Back")}
        </button>
        <h1
          style={{
            color: "#14201e",
            fontSize: 26,
            fontWeight: 800,
            lineHeight: 1.2,
            margin: "0 0 8px",
            letterSpacing: "-0.02em",
          }}
        >
          {_t("groupThisCheck", "Group this check")}
        </h1>
        <p
          style={{
            color: "#5a6462",
            fontSize: 13.5,
            lineHeight: 1.45,
            margin: "0 0 28px",
          }}
        >
          {_t(
            "groupThisCheckSubtitle",
            "If this tenant comes with a guarantor or other tenants, add them here. You'll fill in their checks yourself."
          )}
        </p>

        {isEditingTenant ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 18,
            }}
          >
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={_t("fullName", "Full name")}
              autoFocus
              style={{
                flex: 1,
                minWidth: 0,
                height: 42,
                border: "1.5px solid #14201e",
                borderRadius: 12,
                padding: "0 14px",
                fontSize: 14,
                outline: 0,
              }}
            />
            <input
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              placeholder="email@example.com"
              style={{
                flex: 1,
                minWidth: 0,
                height: 42,
                border: "1px solid #e8e8ef",
                borderRadius: 12,
                padding: "0 14px",
                fontSize: 14,
                outline: 0,
                background: "#fff",
              }}
            />
            <button
              type="button"
              disabled={editSaving}
              onClick={saveTenantEdit}
              style={{
                height: 42,
                padding: "0 22px",
                borderRadius: 12,
                border: 0,
                background: "#1a2e2a",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                opacity: editSaving ? 0.7 : 1,
                flexShrink: 0,
              }}
            >
              {_t("save", "Save")}
            </button>
            <button
              type="button"
              onClick={() => setIsEditingTenant(false)}
              aria-label={_t("cancel", "Cancel")}
              style={{
                width: 32,
                height: 32,
                display: "grid",
                placeItems: "center",
                border: 0,
                background: "transparent",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <img
                src="/images/delete-icon.svg"
                alt=""
                style={{ width: 16, height: 16, opacity: 0.6 }}
              />
            </button>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "#f0f0f0",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                color: "#333",
                flexShrink: 0,
              }}
            >
              {tenantInitial}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong
                  style={{ fontSize: 15, color: "#14201e", lineHeight: 1.2 }}
                >
                  {tenantName || "Javier Moreno"}
                </strong>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: 20,
                    background: "#f0f0f0",
                    borderRadius: "999px",
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: 1,
                    padding: "0 8px",
                    color: "#555",
                  }}
                >
                  {_t("tenant", "Tenant")}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "#6b7674",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {tenantEmail || "javier.moreno@gmail.com"}
              </div>
            </div>
            <button
              type="button"
              onClick={startEditTenant}
              style={{
                background: "none",
                border: 0,
                cursor: "pointer",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: "#14201e",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              ✎ {_t("edit", "Edit")}
            </button>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          {guarantor ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: "#f8f8f8",
                borderRadius: 12,
                padding: "10px 12px",
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {guarantor?.fname?.charAt(0) || "G"}
              </span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: 13 }}>
                  {[guarantor.fname, guarantor.lname].filter(Boolean).join(" ")}
                </strong>
                <div style={{ fontSize: 12, color: "#666" }}>
                  {guarantor.email}
                </div>
              </div>
            </div>
          ) : (
            <GuarantorInviteSection
              currentUserEmail={originalUser?.email}
              invites={guarantorInvites}
              onChange={setGuarantorInvites}
              _t={_t}
              guarantorOptionalKey="guarantorOptionalForCheck"
            />
          )}
        </div>

        {/* Tenant invites — same as CreateApplicationStep */}
        {tenantInvites.map((invite) => (
          <div
            key={invite.email}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "#f0f0f0",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {invite.name.charAt(0)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: 14, lineHeight: 1.2 }}>
                {invite.name}
              </strong>
              <div
                style={{
                  fontSize: 12.5,
                  color: "#6b7674",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {invite.email}
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                setTenantInvites((c) =>
                  c.filter((i) => i.email !== invite.email)
                )
              }
              aria-label={_t("removeTenant", "Remove tenant")}
              style={{
                width: 30,
                height: 30,
                display: "grid",
                placeItems: "center",
                border: 0,
                background: "transparent",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        ))}

        {openTenantForm ? (
          <div className={styles.inviteForm} style={{ marginBottom: 12 }}>
            <div className={styles.inviteFormHeader}>
              <span className={styles.inviteFormBadge}>
                {_t("tenant", "Tenant")}
              </span>
            </div>
            <input
              value={tenantDraft.name}
              autoFocus
              onChange={(e) =>
                setTenantDraft((c) => ({
                  ...c,
                  name: e.target.value,
                  error: "",
                }))
              }
              onKeyDown={(e) =>
                e.key === "Enter" && (e.preventDefault(), addTenantInvite())
              }
              placeholder={_t("fullName", "Full name")}
              aria-label={_t("fullName", "Full name")}
            />
            <input
              type="email"
              value={tenantDraft.email}
              onChange={(e) =>
                setTenantDraft((c) => ({
                  ...c,
                  email: e.target.value.toLowerCase(),
                  error: "",
                }))
              }
              onKeyDown={(e) =>
                e.key === "Enter" && (e.preventDefault(), addTenantInvite())
              }
              placeholder="email@example.com"
              aria-label="Email"
            />
            <button
              className={styles.addButton}
              type="button"
              onClick={addTenantInvite}
              disabled={!tenantDraft.name.trim() || !tenantDraft.email.trim()}
            >
              {_t("add", "Add")}
            </button>
            <button
              className={styles.deleteButton}
              type="button"
              onClick={() => {
                setOpenTenantForm(false);
                setTenantDraft({ name: "", email: "", error: "" });
              }}
              aria-label={_t("remove", "Remove")}
            >
              <img src="/images/delete-icon.svg" alt="" />
            </button>
            {tenantDraft.error && (
              <p className={styles.formError}>{tenantDraft.error}</p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpenTenantForm(true)}
            disabled={tenantInvites.length >= 4}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "none",
              border: 0,
              cursor: tenantInvites.length >= 4 ? "not-allowed" : "pointer",
              padding: "6px 0",
              width: "100%",
              textAlign: "left",
              opacity: tenantInvites.length >= 4 ? 0.5 : 1,
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                border: "1px dashed #d8d8dc",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                color: "#555",
                lineHeight: 1,
              }}
            >
              +
            </span>
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#14201e",
                lineHeight: 1.2,
              }}
            >
              {_t("addTenant", "Add tenant")}
            </span>
          </button>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 28,
            borderTop: "1px solid #eee",
            paddingTop: 18,
          }}
        >
          <button
            type="button"
            disabled={guarantorSubmitting}
            onClick={continueFromGuarantorInvites}
            style={{
              background: "#1a2e2a",
              border: 0,
              borderRadius: 12,
              color: "#fff",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 14,
              lineHeight: 1,
              padding: "13px 20px",
              opacity: guarantorSubmitting ? 0.7 : 1,
            }}
          >
            {guarantorSubmitting
              ? _t("processing", "Processing…")
              : _t("finish", "Finish")}
          </button>
        </div>
      </section>
    </div>
  );
}
